// ─────────────────────────────────────────────
// Fertilizer Recommendation Calculation
//
// Two separate strategies for different crop types:
//
// 1. CEREALS (Maize, Sorghum):
//    → calculateCerealFertilizerRecommendation()
//    → Nitrogen is the limiting nutrient
//    → NPK is the primary fertilizer
//    → Urea is used for nitrogen supplementation
//
// 2. LEGUMES (Cowpea, Soybean):
//    → calculateLegumeFertilizerRecommendation()
//    → Phosphorus is the limiting nutrient
//    → NPK is the primary fertilizer
//    → SSP is used for phosphorus supplementation
//
// The main function, calculateCropFertilizerApplication(), determines which strategy to use
// based on the selected crop and then builds a user-friendly recommendation message.
// ─────────────────────────────────────────────

import { getCowpeaAndSoyabeanValues, getMaizeValues, getSorghumValues } from "./cropNutrientValue";

// ════════════════════════════════════════════
// BLEND NUTRIENT CONTENT
// How much N, P, K each 50kg bag of fertilizer supplies
// ════════════════════════════════════════════

// Nutrient supplied by one 50 kg bag of each fertilizer blend.
// Example:
// 20:10:10 means 20% N, 10% P, 10% K in the bag
//   → 50kg × (20/100) = 10 kg N
//   → 50kg × (10/100) = 5 kg P
//   → 50kg × (10/100) = 5 kg K
//
// 15:15:15 means 15% N, 15% P, 15% K in the bag
//   → 50kg × (15/100) = 7.5 kg N
//   → 50kg × (15/100) = 7.5 kg P
//   → 50kg × (15/100) = 7.5 kg K
const BLEND_NUTRIENTS_PER_BAG = {
  "20:10:10": { n: 10, p: 5, k: 5 },
  "15:15:15": { n: 7.5, p: 7.5, k: 7.5 },
};

// ════════════════════════════════════════════
// SUPPLEMENTARY FERTILIZER CONTENT
// How much of the limiting nutrient each fertilizer supplies
// ════════════════════════════════════════════

// Urea fertilizer: each 50kg bag contains 46% nitrogen
// So: 50kg × 0.46 = 23kg of nitrogen per bag
// Used to supplement nitrogen shortfalls in cereals
const UREA_N_PER_BAG = 23;

// SSP (Single Super Phosphate): each 50kg bag contains 16% phosphorus
// So: 50kg × 0.16 = 8kg of phosphorus per bag
// Used to supplement phosphorus shortfalls in legumes
const SSP_P_PER_BAG = 8;

// ════════════════════════════════════════════
// CEREAL FERTILIZER CALCULATION
// For: Maize, Sorghum
// Strategy: Nitrogen is the limiting nutrient
// ════════════════════════════════════════════

const calculateCerealFertilizerApplication = ({
  cropNutrientValue, // { nitrogen, phosphorus, potassium }
  fertilizerBlend, // "20:10:10" or "15:15:15"
}) => {
  // ── STEP 1: Get the nutrient content of the selected fertilizer blend ──
  // Example: 20:10:10 means one bag supplies 10 kg N, 5 kg P, and 5 kg K.
  const blendNutrientValue = BLEND_NUTRIENTS_PER_BAG[fertilizerBlend];

  // If the crop nutrient values or fertilizer blend are missing,
  // we cannot continue with the calculation.
  if (!cropNutrientValue || !blendNutrientValue) {
    return null;
  }

  // ── STEP 2: Extract the nutrient requirements for the selected crop ──
  // Example: maize may require:
  // nitrogen = 120, phosphorus = 0, potassium = 30.
  const { nitrogen, phosphorus, potassium } = cropNutrientValue;

  // If any of the crop nutrient values are missing,
  // we cannot calculate the fertilizer recommendation.
  if (nitrogen === undefined || phosphorus === undefined || potassium === undefined) {
    return null;
  }

  // ── STEP 3: Determine NPK bags needed using P and K requirements ──
  // For cereals, we look at phosphorus and potassium, then use the LARGER value
  // to decide how many bags of NPK fertilizer are needed.
  // Why? Because we want to meet BOTH P and K requirements simultaneously.
  //
  // Example:
  // If P = 0 and K = 30, then the larger value is 30, so we calculate
  // how many bags of NPK are needed to meet the 30 kg K requirement.
  const largerValueBetweenPAndK = Math.max(phosphorus, potassium);

  // ── STEP 4: Calculate the number of NPK bags ──
  // If both phosphorus and potassium are zero, no NPK is needed.
  // Otherwise, divide the larger value by the amount of that nutrient
  // supplied by one bag of the selected blend.
  //
  // Example: if K = 30 and one bag of 20:10:10 supplies 5 kg K, then:
  // 30 / 5 = 6 bags of NPK needed
  const bagsOfNpk =
    largerValueBetweenPAndK === 0
      ? 0
      : Math.ceil(largerValueBetweenPAndK / Math.max(blendNutrientValue.p, blendNutrientValue.k));

  // ── STEP 5: Calculate how much N, P, K those NPK bags will supply ──
  // Once we know how many bags, multiply by the nutrient content per bag.
  //
  // Example: 6 bags of 20:10:10 will supply:
  // N = 6 × 10 = 60 kg
  // P = 6 × 5 = 30 kg
  // K = 6 × 5 = 30 kg
  const suppliedByNpk = {
    n: bagsOfNpk * blendNutrientValue.n,
    p: bagsOfNpk * blendNutrientValue.p,
    k: bagsOfNpk * blendNutrientValue.k,
  };

  // ── STEP 6: Check for nitrogen deficit ──
  // See if the nitrogen supplied by NPK bags is enough to meet the crop requirement.
  // If there is a shortfall, calculate how much nitrogen is missing.
  //
  // Example: crop needs 120 kg N, but NPK supplies only 60 kg N
  // Deficit = 120 - 60 = 60 kg N
  const nitrogenDeficit = Math.max(nitrogen - suppliedByNpk.n, 0);

  // ── STEP 7: Calculate Urea bags to cover nitrogen deficit ──
  // Use Urea ONLY when there is a nitrogen deficit.
  // Since one bag of Urea supplies 23 kg N, divide the deficit by 23
  // and round up to the next whole bag.
  //
  // Example: 60 kg N deficit ÷ 23 kg per bag = 2.6 bags → round up to 3 bags
  const bagsOfUrea = nitrogenDeficit === 0 ? 0 : Math.ceil(nitrogenDeficit / UREA_N_PER_BAG);

  // ── RETURN the fertilizer recommendation ──
  return {
    bagsOfNpk, // Number of NPK fertilizer bags to apply
    bagsOfUrea, // Number of Urea bags for nitrogen top-up
    suppliedByNpk, // What the NPK bags provide { n, p, k }
    nitrogenDeficit, // Gap that Urea will fill (kg)
  };
};

// ════════════════════════════════════════════
// LEGUME FERTILIZER CALCULATION
// For: Cowpea, Soybean
// Strategy: Phosphorus is the limiting nutrient
// (Legumes fix their own nitrogen from the air)
// ════════════════════════════════════════════

const calculateLegumeFertilizerApplication = ({
  cropNutrientValue, // { nitrogen, phosphorus, potassium }
  fertilizerBlend, // "20:10:10" or "15:15:15"
}) => {
  // ── STEP 1: Get the nutrient content of the selected fertilizer blend ──
  // Example: 20:10:10 means one bag supplies 10 kg N, 5 kg P, and 5 kg K.
  const blendNutrientValue = BLEND_NUTRIENTS_PER_BAG[fertilizerBlend];

  // If the crop nutrient values or fertilizer blend are missing,
  // we cannot continue with the calculation.
  if (!cropNutrientValue || !blendNutrientValue) {
    return null;
  }

  // ── STEP 2: Extract the nutrient requirements for the selected crop ──
  // Example: cowpea may require:
  // nitrogen = 20 (low, since it fixes its own), phosphorus = 40, potassium = 20.
  const { nitrogen, phosphorus, potassium } = cropNutrientValue;

  // If any of the crop nutrient values are missing,
  // we cannot calculate the fertilizer recommendation.
  if (nitrogen === undefined || phosphorus === undefined || potassium === undefined) {
    return null;
  }

  // ── STEP 3: Determine NPK bags needed using N and K requirements ──
  // For LEGUMES, phosphorus is the limiting nutrient (we'll check this separately).
  // Instead, we use NITROGEN and POTASSIUM to determine how many NPK bags are needed.
  // We look at nitrogen and potassium, then use the LARGER value.
  // Why? Because we want to meet BOTH N and K requirements simultaneously.
  // (We don't focus on P here because we'll supplement P with SSP if needed)
  //
  // Example:
  // If N = 20 and K = 20, then the larger value is 20, so we calculate
  // how many bags of NPK are needed to meet the 20 kg requirement.
  //
  // Another example:
  // If N = 20 and K = 30, then the larger value is 30, so we calculate
  // how many bags of NPK are needed to meet the 30 kg K requirement.
  const largerValueBetweenNAndK = Math.max(nitrogen, potassium);

  // ── STEP 4: Calculate the number of NPK bags ──
  // If both nitrogen and potassium are zero, no NPK is needed.
  // Otherwise, divide the larger value by the amount of that nutrient
  // supplied by one bag of the selected blend.
  //
  // Example: if K = 30 and one bag of 20:10:10 supplies 5 kg K, then:
  // 30 / 5 = 6 bags of NPK needed
  const bagsOfNpk =
    largerValueBetweenNAndK === 0
      ? 0
      : Math.ceil(largerValueBetweenNAndK / Math.max(blendNutrientValue.n, blendNutrientValue.k));

  // ── STEP 5: Calculate how much N, P, K those NPK bags will supply ──
  // Once we know how many bags, multiply by the nutrient content per bag.
  //
  // Example: 6 bags of 20:10:10 will supply:
  // N = 6 × 10 = 60 kg
  // P = 6 × 5 = 30 kg
  // K = 6 × 5 = 30 kg
  const suppliedByNpk = {
    n: bagsOfNpk * blendNutrientValue.n,
    p: bagsOfNpk * blendNutrientValue.p,
    k: bagsOfNpk * blendNutrientValue.k,
  };

  // ── STEP 6: Check for phosphorus deficit ──
  // For LEGUMES, phosphorus is the critical nutrient (NOT nitrogen).
  // See if the phosphorus supplied by NPK bags is enough to meet the crop requirement.
  // If there is a shortfall, calculate how much phosphorus is missing.
  //
  // Example: crop needs 40 kg P, but NPK supplies only 30 kg P
  // Deficit = 40 - 30 = 10 kg P
  const phosphorusDeficit = Math.max(phosphorus - suppliedByNpk.p, 0);

  // ── STEP 7: Calculate SSP bags to cover phosphorus deficit ──
  // Use SSP ONLY when there is a phosphorus deficit.
  // Since one bag of SSP supplies 8 kg P, divide the deficit by 8
  // and round up to the next whole bag.
  //
  // Example: 10 kg P deficit ÷ 8 kg per bag = 1.25 bags → round up to 2 bags
  const bagsOfSSP = phosphorusDeficit === 0 ? 0 : Math.ceil(phosphorusDeficit / SSP_P_PER_BAG);

  // ── RETURN the fertilizer recommendation ──
  return {
    bagsOfNpk, // Number of NPK fertilizer bags to apply
    bagsOfSSP, // Number of SSP bags for phosphorus top-up
    suppliedByNpk, // What the NPK bags provide { n, p, k }
    phosphorusDeficit, // Gap that SSP will fill (kg)
  };
};

// Calculates the crop-based fertilizer application based on the selected crop, blend, and soil data.
const calculateCropFertilizerApplication = (
  recommendation,
  selectedCrop,
  selectedBlend,
  setCropRecommendation,
) => {
  // ════════════════════════════════════════
  // STEP 1: Collect the nutrient status
  // ════════════════════════════════════════
  // Extract the nutrient status values returned
  // from the soil recommendation engine.
  //
  // Example:
  // {
  //   nitrogenStatus: "low",
  //   phosphorusStatus: "medium",
  //   potassiumStatus: "high"
  // }

  // This helper function cleans and standardizes, the soil nutrient status values coming from the API.
  // Why this is needed: Different systems may return different words for the same meaning.
  //
  // Example: VERY_LOW", "very-low", "very low" all mean the same thing.
  //
  // But our fertilizer tables only understand: "low", "medium", and "high"
  //
  // So this function converts everything into one consistent format the app can understand.
  const normalizeSoilStatus = (soilStatus) => {
    // If there is no soil status value, stop and return null.
    if (!soilStatus) return null;

    // Convert the value into lowercase text so all comparisons become consistent.
    //
    // Example: "HIGH" becomes "high". VeryLow" becomes "verylow"
    const cleanedStatus = String(soilStatus).toLowerCase();

    // Different versions of "very low" should all be treated as simply "low"
    if (
      cleanedStatus === "verylow" ||
      cleanedStatus === "very_low" ||
      cleanedStatus === "very-low" ||
      cleanedStatus === "very low"
    ) {
      return "low";
    }

    // If the value is already "low", return it unchanged.
    if (cleanedStatus === "low") {
      return "low";
    }

    // Convert shortened versions like "med" into the standard "medium"
    if (cleanedStatus === "medium" || cleanedStatus === "med") {
      return "medium";
    }

    // Some systems may use "adequate" instead of "high"
    if (cleanedStatus === "high" || cleanedStatus === "adequate") {
      return "high";
    }

    // If the value is unknown, return null so the app knows the data is invalid.
    return null;
  };

  const nutrientStatuses = {
    nitrogenStatus: normalizeSoilStatus(recommendation?.nitrogen?.code),
    phosphorusStatus: normalizeSoilStatus(recommendation?.phosphorus?.code),
    potassiumStatus: normalizeSoilStatus(recommendation?.potassium?.code),
  };

  // If soil recommendation data is missing.
  if (
    !recommendation ||
    !recommendation.nitrogen ||
    !recommendation.phosphorus ||
    !recommendation.potassium
  ) {
    setCropRecommendation(
      "Please select a map location with soil data before calculating recommendations.",
    );
    return;
  }

  // This variable will later hold the nutrient
  // requirement values for the selected crop.
  //
  // Example for maize:
  // {
  //   nitrogen: 120,
  //   phosphorus: 60,
  //   potassium: 30
  // }
  let cropNutrientValue;

  // ════════════════════════════════════════
  // STEP 2: Determine crop nutrient requirement
  // ════════════════════════════════════════
  // Each crop has its own fertilizer recommendation table.
  // Based on the soil nutrient status (low/medium/high),
  // we calculate the nutrient requirement for that crop.

  // Maize recommendation values
  if (selectedCrop === "maize") {
    cropNutrientValue = getMaizeValues(nutrientStatuses);
  }

  // Sorghum recommendation values
  if (selectedCrop === "sorghum") {
    cropNutrientValue = getSorghumValues(nutrientStatuses);
  }

  // Cowpea and Soybean recommendation values
  // These crops share the same recommendation table
  // because both are legumes.
  if (selectedCrop === "cowpea" || selectedCrop === "soyabean") {
    cropNutrientValue = getCowpeaAndSoyabeanValues(nutrientStatuses);
  }

  // ════════════════════════════════════════
  // STEP 3: Identify crop category
  // ════════════════════════════════════════
  const isCereal = selectedCrop === "maize" || selectedCrop === "sorghum";

  const isLegume = selectedCrop === "cowpea" || selectedCrop === "soyabean";

  // This will store the final fertilizer calculation result.
  let fertilizerRecommendation;

  // ════════════════════════════════════════
  // STEP 4: Run fertilizer calculation
  // ════════════════════════════════════════

  // The fertilizer calculation logic is different for cereals and legumes.
  if (isCereal) {
    fertilizerRecommendation = calculateCerealFertilizerApplication({
      cropNutrientValue,
      fertilizerBlend: selectedBlend,
    });
  }

  if (isLegume) {
    fertilizerRecommendation = calculateLegumeFertilizerApplication({
      cropNutrientValue,
      fertilizerBlend: selectedBlend,
    });
  }

  // ════════════════════════════════════════
  // STEP 5: Handle failed calculation
  // ════════════════════════════════════════
  // If the calculation could not be completed,
  // show an error message and stop execution.
  if (!fertilizerRecommendation) {
    setCropRecommendation("Unable to calculate recommendation for the selected crop and blend.");
    return;
  }

  // ════════════════════════════════════════
  // STEP 6: Build recommendation message
  // ════════════════════════════════════════
  // This array will hold different fertilizer
  // recommendations before joining them into
  // one readable sentence.
  const recommendationParts = [];

  // ════════════════════════════════════════
  // STEP 6A: Build cereal recommendation
  // ════════════════════════════════════════
  if (isCereal) {
    const { bagsOfNpk, bagsOfUrea } = fertilizerRecommendation;

    // Add NPK recommendation if required
    if (bagsOfNpk > 0) {
      recommendationParts.push(
        `${bagsOfNpk} bag${bagsOfNpk > 1 ? "s" : ""} of NPK ${selectedBlend}`,
      );
    }

    // Add Urea recommendation if required
    if (bagsOfUrea > 0) {
      recommendationParts.push(`${bagsOfUrea} bag${bagsOfUrea > 1 ? "s" : ""} of Urea`);
    }
  }

  // ════════════════════════════════════════
  // STEP 6B: Build legume recommendation
  // ════════════════════════════════════════
  if (isLegume) {
    const { bagsOfNpk, bagsOfSSP } = fertilizerRecommendation;

    // Add NPK recommendation if required
    if (bagsOfNpk > 0) {
      recommendationParts.push(
        `${bagsOfNpk} bag${bagsOfNpk > 1 ? "s" : ""} of NPK ${selectedBlend}`,
      );
    }

    // Add SSP recommendation if required
    if (bagsOfSSP > 0) {
      recommendationParts.push(`${bagsOfSSP} bag${bagsOfSSP > 1 ? "s" : ""} of SSP`);
    }
  }

  // ════════════════════════════════════════
  // STEP 7: Handle zero fertilizer case
  // ════════════════════════════════════════
  // If no fertilizer is required at all,
  // show a simple message and stop execution.
  if (recommendationParts.length === 0) {
    setCropRecommendation("No fertilizer application is required.");
    return;
  }

  // ════════════════════════════════════════
  // STEP 8: Display final recommendation
  // ════════════════════════════════════════
  // Join all recommendation parts into one sentence.
  //
  // Example:
  // "Apply 6 bags of NPK 20:10:10 + 3 bags of Urea per hectare."
  setCropRecommendation(recommendationParts.join(" + "));
};

export default calculateCropFertilizerApplication;
